import React, { useEffect, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import styled from 'styled-components';
import { get } from 'lodash';
import {
  QuestionLabel,
  FieldWrapper,
  Input,
  RadioInput,
  RadioLabel,
  SecondaryButton,
  Error,
} from './components';
import { Flex, Text } from '../components';
import Axios from '../axios';

const DeleteBtn = styled.img`
  cursor: pointer;
`;

function DirectDepositQuestions({
  disabled, account, isCompleted, idx,
}) {
  const {
    register, clearErrors, setError, getValues, errors, setValue,
  } = useFormContext();

  const baseRules = {
    required: 'This is required',
  };

  const [bankName, setBankName] = useState(account.bank_name || null);

  const [percentage, setPercentage] = useState(`${account.percentage}` || null);

  useEffect(() => {
    let defaultPercentage = `${account.percentage}` || '';
    if (defaultPercentage.length > 0 && !defaultPercentage.includes('%')) {
      defaultPercentage = `${defaultPercentage}%`;
      setPercentage(defaultPercentage);
    }
  }, [account]);

  let currentBankName = null;
  const currentFormValues = getValues();
  if (currentFormValues.accounts) {
    const currentAccountValues = currentFormValues.accounts[account.id];
    if (currentAccountValues && currentAccountValues.bank_name) {
      currentBankName = currentAccountValues.bank_name;
    }
  }

  useEffect(() => {
    if (currentBankName) {
      setBankName(currentBankName);
      setValue(`accounts.${idx}.bank_name`, currentBankName);
    }
  }, [currentBankName]);

  const searchBank = (routingNumber) => {
    if (!(/^\d{9}$/.test(routingNumber))) return;

    const config = {
      'Access-Control-Allow-Origin': '*',
    };

    const searchAPI = `/j/fed-wire?routing_number=${routingNumber}`;
    Axios.get(searchAPI, config)
      .then(({ data }) => {
        const { bankName: bankNameResult } = data;
        setBankName(bankNameResult);
        setValue(`accounts.${idx}.bank_name`, bankNameResult);
        clearErrors([`accounts.${idx}.routing_number`]);
      })
      .catch((e) => {
        setBankName(null);
        setValue(`accounts.${idx}.bank_name`, null);
        setError(`accounts.${idx}.routing_number`, {
          type: 'manual',
          message: 'The routing number is invalid',
        });
      });
  };

  useEffect(() => {
    if (!bankName && account.routing_number && !account.bank_name) {
      searchBank(account.routing_number);
    }
  }, [account, searchBank]);

  const renderBank = () => {
    if (bankName) {
      return (
        <Flex alignItems="center" marginTop="12px">
          <img src="/j/images/employee-onboarding/bank.svg" />
          <Text marginLeft="9px" color="gray50" fontWeight="600" fontSize="12px">
            {bankName}
          </Text>
        </Flex>
      );
    }
    return null;
  };

  const addPercentageMark = (valueText) => {
    let formattedText = valueText;
    if (valueText.length > 0 && !valueText.includes('%')) {
      formattedText = `${valueText}%`;
    }
    setPercentage(formattedText);
  };

  const removePercentageMark = (valueText) => {
    const numberValue = valueText.replace('%', '');
    setPercentage(numberValue);
  };

  return (
    <Flex column style={{ width: '100%', justifyContent: 'space-between' }}>
      <FieldWrapper>
        <QuestionLabel isRequired={false}>Account Type</QuestionLabel>
        <Flex column>
          <Flex alignItems="center">
            <RadioInput
              id={`${account.id}-account-checking`}
              type="radio"
              name={`accounts.${idx}.account_type`}
              data-testid={`${account.id}-account-checking`}
              disabled={disabled}
              value="checking"
              defaultChecked={account.account_type === 'checking'}
              ref={register()}
            />
            <RadioLabel htmlFor={`${account.id}-account-checking`}>Checking account</RadioLabel>
          </Flex>
          <Flex alignItems="center">
            <RadioInput
              id={`${account.id}-account-savings`}
              type="radio"
              name={`accounts.${idx}.account_type`}
              data-testid={`${account.id}-account-savings`}
              value="savings"
              disabled={disabled}
              defaultChecked={account.account_type === 'savings'}
              ref={register()}
            />
            <RadioLabel htmlFor={`${account.id}-account-savings`}>Savings account</RadioLabel>
          </Flex>
        </Flex>
      </FieldWrapper>
      <FieldWrapper>
        <QuestionLabel isRequired={false}>Routing number</QuestionLabel>
        <Input
          type="text"
          name={`accounts.${idx}.routing_number`}
          data-testid="direct-deposit-routing-number-input"
          ref={register({
            ...baseRules,
            pattern: {
              value: /^\d{9}$/,
              message: 'Routing number must be 9 digits',
            },
            validate: () => (bankName && bankName.length > 0 ? true : 'The routing number is invalid'),
          })}
          onBlur={(e) => {
            searchBank(e.target.value);
          }}
          defaultValue={account.routing_number}
          disabled={disabled}
        />
        {!bankName && !isCompleted && !get(errors, `accounts.${idx}.routing_number`) && (
          <Text
            style={{
              fontSize: '12px',
              color: '#68728A',
            }}
          >
            Routing number is 9 digits
          </Text>
        )}
        <Input
          type="hidden"
          defaultValue={bankName}
          name={`accounts.${idx}.bank_name`}
          ref={register()}
        />
        <Error errors={errors} fieldName={`accounts.${idx}.routing_number`} />

        {renderBank()}
      </FieldWrapper>
      <FieldWrapper>
        <QuestionLabel isRequired={false}>Account number</QuestionLabel>
        <Input
          type="text"
          data-testid="direct-deposit-account-number-input"
          name={`accounts.${idx}.account_number`}
          ref={register({
            ...baseRules,
            pattern: {
              value: /^\d{4,17}$/,
              message: 'Account number must be 4-17 digits',
            },
          })}
          defaultValue={account.account_number}
          disabled={disabled}
        />
        <Error errors={errors} fieldName={`accounts.${idx}.account_number`} />
      </FieldWrapper>
      {!isCompleted && (
        <FieldWrapper>
          <QuestionLabel isRequired={false}>Confirm account number</QuestionLabel>
          <Input
            type="text"
            data-testid="direct-deposit-confirm-account-number-input"
            name={`accounts.${idx}.confirm_account_number`}
            defaultValue={account.confirm_account_number}
            ref={register({
              ...baseRules,
              pattern: {
                value: /^\d{4,17}$/,
                message: 'Account number must be 4-17 digits',
              },
              validate: (value) => {
                const accountNumber = getValues(`accounts.${idx}.account_number`);
                if (accountNumber !== value) {
                  return 'Please make sure your account number match';
                }
                return true;
              },
            })}
            onPaste={(e) => {
              e.preventDefault();
              setError(`accounts.${idx}.confirm_account_number`, {
                type: 'manual',
                message: 'Please manually type your account number',
              });
            }}
            disabled={disabled}
          />
          <Error errors={errors} fieldName={`accounts.${idx}.confirm_account_number`} />
        </FieldWrapper>
      )}
      <FieldWrapper>
        <QuestionLabel isRequired={false}>
          Percentage of paycheck you want to receive in this account
        </QuestionLabel>
        <Input
          type="text"
          placeholder="%"
          style={{ width: '120px' }}
          data-testid="direct-deposit-percentage-of-paycheck-input"
          disabled={disabled}
          name={`accounts.${idx}.percentage`}
          value={percentage}
          onChange={(e) => {
            const { value } = e.target;
            setPercentage(value);
          }}
          onBlur={(e) => {
            const { value } = e.target;
            addPercentageMark(value);
          }}
          onFocus={(e) => {
            const { value } = e.target;
            removePercentageMark(value);
          }}
          ref={register({
            validate: (value) => {
              const transformedValue = value.replace('%', '');
              setPercentage(value);
              if (transformedValue.length === 0) {
                return 'This is required';
              }
              if (/^\d+$/.test(transformedValue) === false) {
                return 'Only digits allowed';
              }
              if (parseInt(transformedValue) === 0) {
                return 'Cannot be zero';
              }
              if (parseInt(transformedValue) > 100) {
                return 'Cannot be more than 100';
              }
              return true;
            },
          })}
        />
        <Error errors={errors} fieldName={`accounts.${idx}.percentage`} />
      </FieldWrapper>
    </Flex>
  );
}

function DirectDepositForm({ allowMultipleAccounts, disabled, isCompleted }) {
  const { getValues, control } = useFormContext();
  const { fields, remove, append } = useFieldArray({
    name: 'accounts',
    control,
  });

  return (
    <Flex column>
      {fields.map((account, idx) => (
        <div key={account.id}>
          <Flex
            style={{
              justifyContent: 'space-between',
              borderTop: idx === 0 ? 'none' : '1px solid #D4D4D8',
              paddingTop: '20px',
            }}
          >
            <Text fontWeight='600' color='#464D5D' fontSize='16px' style={{ marginBottom: '12px' }}>
              Bank account
            </Text>
            {allowMultipleAccounts && !isCompleted && fields.length > 1 && (
              <DeleteBtn
                src='/j/images/employee-onboarding/bin.svg'
                onClick={() => {
                  remove(idx);
                }}
              />
            )}
          </Flex>
          <Flex style={{ paddingBottom: '5px' }}>
            <DirectDepositQuestions
              disabled={disabled}
              account={account}
              isCompleted={isCompleted}
              idx={idx}
            />
          </Flex>
        </div>
      ))}
      {allowMultipleAccounts && fields.length < 3 && !isCompleted && (
        <SecondaryButton
          style={{ height: '34px' }}
          data-testid='direct-deposit-add-another-account-btn'
          onClick={(e) => {
            e.preventDefault();
            const { accounts: existingAccounts } = getValues();
            const percentageSum = existingAccounts.reduce(
              (acc, cur) => acc + parseInt(cur.percentage),
              0
            );
            const newAccount = {
              id: Date.now(),
              account_type: 'checking',
              percentage: percentageSum < 100 ? `${100 - percentageSum}%` : '',
            };
            append(newAccount);
          }}
        >
          <Flex style={{ alignItems: 'center' }}>
            <img src='/j/images/employee-onboarding/add.svg' />
            <Text style={{ paddingLeft: '5px' }} semibold>
              Add another account
            </Text>
          </Flex>
        </SecondaryButton>
      )}
    </Flex>
  );
}

export default DirectDepositForm;
